import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  writeBatch,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
  PayrollImportDocument,
  PayrollImportCollaboratorSummary,
  PayrollImportRecord,
  PayrollRun,
} from './payrollTypes';

const PAYROLL_IMPORTS_COLLECTION = 'payroll_imports';

function getCollectionRef() {
  return collection(db, PAYROLL_IMPORTS_COLLECTION);
}

function mapCollaborators(data: any): PayrollImportCollaboratorSummary[] {
  if (!Array.isArray(data)) return [];

  return data
    .map((item) => ({
      collaboratorId: item?.collaboratorId ?? '',
      collaboratorName: item?.collaboratorName ?? 'Sem nome',
      payrollEmployeeId: item?.payrollEmployeeId ?? '',
      earningsTotal: typeof item?.earningsTotal === 'number' ? item.earningsTotal : 0,
      deductionsTotal: typeof item?.deductionsTotal === 'number' ? item.deductionsTotal : 0,
      payrollSalary: typeof item?.payrollSalary === 'number' ? item.payrollSalary : item?.payrollSalary ?? null,
    }))
    .filter((item) => item.collaboratorId);
}

function mapPayrollImportDocument(
  document: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>,
): PayrollImportDocument | null {
  const data = document.data();
  if (!data) return null;

  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();

  const competence =
    typeof data.competence === 'string' || typeof data.competence === 'number'
      ? String(data.competence)
      : undefined;
  const competenceYear =
    typeof data.competenceYear === 'string' || typeof data.competenceYear === 'number'
      ? String(data.competenceYear)
      : undefined;
  const competenceMonth =
    typeof data.competenceMonth === 'string' || typeof data.competenceMonth === 'number'
      ? String(data.competenceMonth)
      : undefined;
  const run = (data.run ?? null) as PayrollRun | null;
  const collaborators = mapCollaborators(data.collaborators);
  const totals = data.totals ?? undefined;
  const audit = data.audit ?? undefined;

  const createdByUid = typeof data.createdByUid === 'string' ? data.createdByUid : '';
  const createdByEmail = typeof data.createdByEmail === 'string' ? data.createdByEmail : null;
  const createdByName = typeof data.createdByName === 'string' ? data.createdByName : null;

  if (!createdByUid) {
    console.warn('[payrollImportsService] Documento sem createdByUid ignorado', document.id);
    return null;
  }

  return {
    id: document.id,
    run,
    competence,
    competenceYear,
    competenceMonth,
    collaborators,
    totals,
    createdAt,
    createdByUid,
    createdByEmail,
    createdByName,
    audit,
  };
}

export async function listPayrollImports(): Promise<PayrollImportDocument[]> {
  const importsQuery = query(getCollectionRef(), orderBy('createdAt', 'desc'));
  const snap = await getDocs(importsQuery);

  return snap.docs.map(mapPayrollImportDocument).filter(Boolean) as PayrollImportDocument[];
}

export async function listPayrollImportsByCompetence(targetCompetence: string): Promise<PayrollImportDocument[]> {
  const competence = (targetCompetence ?? '').trim();
  if (!competence) return [];

  const fetchByQuery = async (constraints: Array<ReturnType<typeof where>>) => {
    const snap = await getDocs(query(getCollectionRef(), ...constraints));
    return snap.docs.map(mapPayrollImportDocument).filter(Boolean) as PayrollImportDocument[];
  };

  const byCompetence = await fetchByQuery([where('competence', '==', competence)]);
  if (byCompetence.length) return byCompetence;

  const normalized = competence.replace(/\D/g, '');
  const year = normalized.slice(0, 4);
  const month = normalized.slice(4, 6);
  if (year && month) {
    const asString = await fetchByQuery([where('competenceYear', '==', year), where('competenceMonth', '==', month)]);
    if (asString.length) return asString;

    const monthNumber = Number(month);
    const yearNumber = Number(year);
    if (!Number.isNaN(monthNumber) && !Number.isNaN(yearNumber)) {
      const asNumber = await fetchByQuery([
        where('competenceYear', '==', yearNumber),
        where('competenceMonth', '==', monthNumber),
      ]);
      if (asNumber.length) return asNumber;
    }
  }

  return [];
}

export async function getPayrollImportById(importId: string): Promise<PayrollImportDocument | null> {
  const snapshot = await getDoc(doc(db, PAYROLL_IMPORTS_COLLECTION, importId));
  if (!snapshot.exists()) return null;
  return mapPayrollImportDocument(snapshot);
}

export async function getPayrollImportRecord(importId: string, collaboratorId: string): Promise<PayrollImportRecord | null> {
  const recordRef = doc(db, PAYROLL_IMPORTS_COLLECTION, importId, 'records', collaboratorId);
  const snapshot = await getDoc(recordRef);
  if (!snapshot.exists()) return null;

  const data = snapshot.data();
  if (!data) return null;

  return {
    collaboratorId: data.collaboratorId ?? collaboratorId,
    collaboratorName: data.collaboratorName ?? 'Sem nome',
    payrollEmployeeId: data.payrollEmployeeId ?? '',
    matchedBy: data.matchedBy ?? 'employeeId',
    payrollRecord: data.payrollRecord as PayrollImportRecord['payrollRecord'],
  };
}

export async function deletePayrollImportById(importId: string): Promise<void> {
  // Delete subcollection records first (if any), chunking batches to stay within limits.
  const recordsSnap = await getDocs(collection(db, PAYROLL_IMPORTS_COLLECTION, importId, 'records'));

  let batch = writeBatch(db);
  let operations = 0;

  for (const recordDoc of recordsSnap.docs) {
    batch.delete(recordDoc.ref);
    operations += 1;
    if (operations >= 400) {
      // Commit early to avoid approaching the 500 limit.
      await batch.commit();
      batch = writeBatch(db);
      operations = 0;
    }
  }

  if (operations > 0) {
    await batch.commit();
  }

  await deleteDoc(doc(db, PAYROLL_IMPORTS_COLLECTION, importId));
}

export async function removeAuditItem(importId: string, itemToRemove: { employeeId: string; issueType: string }): Promise<void> {
  const docRef = doc(db, PAYROLL_IMPORTS_COLLECTION, importId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return;

  const data = snapshot.data();
  const currentAudit = (data.audit || []) as any[];

  const newAudit = currentAudit.filter((item) =>
    !(item.employeeId === itemToRemove.employeeId && item.issueType === itemToRemove.issueType)
  );

  await updateDoc(docRef, { audit: newAudit });
}
